import { formatWithK, numberWithCommas } from '@/utils/numbers.js'
import { handleError } from '@/services/errorHandler.js'
import { AllMetrics } from '@/services/adMetircs.jsx'
import { buildAssetBreakdownGroups } from '@/services/assetBreakdown.js'
import { buildAdAudienceMatrix } from '@/services/audienceMatrix.js'
import { NAMING_CONVENTION_GROUP_KEY, bucketAdsByNamingConvention, extractNamingConventionValue, getNamingConventionProperty, matchesNamingConventionFilter } from '@/services/namingConvention.js'

// Fast key -> metric lookup, built once. Used by windowed custom metrics to find
// a dependency's Meta extractor without scanning AllMetrics on the hot path.
const metricByKey = new Map(AllMetrics.map(metric => [metric.key, metric]))

// A metric is "windowable" if its value is a summable, Meta action-derived count
// (purchases, revenue, 3s plays, thruplay...). Only these change with the
// attribution window; delivery metrics (impressions, spend) do not.
const isWindowableSummable = (metric) => !!(metric && metric.extractors && metric.extractors.meta) && metric.displaySum === true

// All transitive windowable leaf metrics reachable from a metric's dependency
// graph. E.g. hook_rate -> { 3s_video_plays } (impressions is window-independent).
// Static over metric definitions, so top-level results are memoized.
const windowableLeafCache = new Map()
const collectWindowableLeaves = (metricKey, depth = 0) => {
    if (depth === 0 && windowableLeafCache.has(metricKey)) {
        return windowableLeafCache.get(metricKey)
    }
    const out = new Set()
    if (depth > 8) {
        return out
    }
    const metric = metricByKey.get(metricKey)
    if (metric) {
        if (isWindowableSummable(metric)) {
            out.add(metricKey)
        } else if (!metric.calculateFromBenchmark && Array.isArray(metric.dependsOn)) {
            for (const dep of metric.dependsOn) {
                for (const leaf of collectWindowableLeaves(dep, depth + 1)) {
                    out.add(leaf)
                }
            }
        }
    }
    if (depth === 0) {
        windowableLeafCache.set(metricKey, out)
    }
    return out
}

// Resolve a metric's value for a windowed custom metric:
//  - a windowed leaf (e.g. 3s_video_plays) -> its window-specific sum from the bucket
//  - a derived/ratio metric (e.g. hook_rate) -> recompute via its own
//    calculationFunction from windowed leaves + window-independent totals
//  - anything else (impressions, spend, unknown) -> the normal report-window total
const resolveWindowedMetricValue = (metricKey, windowedBucket, totalEntity, depth = 0) => {
    if (windowedBucket && metricKey in windowedBucket) {
        return windowedBucket[metricKey]
    }
    if (depth <= 8) {
        const metric = metricByKey.get(metricKey)
        if (metric && typeof metric.calculationFunction === 'function' && !metric.calculateFromBenchmark && !isWindowableSummable(metric) && Array.isArray(metric.dependsOn) && metric.dependsOn.length) {
            const synthetic = {}
            for (const dep of metric.dependsOn) {
                synthetic[dep] = resolveWindowedMetricValue(dep, windowedBucket, totalEntity, depth + 1)
            }
            try {
                const val = metric.calculationFunction(synthetic, synthetic)
                if (typeof val === 'number' && isFinite(val)) {
                    return val
                }
            } catch (e) {
                // fall through to the normal total below
            }
        }
    }
    const v = totalEntity[metricKey]
    return typeof v === 'number' ? v : 0
}

const calcualteThroughRate = (impressions, valueToCalcualteThroughRate) => {
    // Decimal rate (e.g., 0.000097...)
    const rate = (num, den) => (den ? num / den : 0);

    // Percentage (e.g., 0.0097%)
    const ratePct = (num, den) => (den ? (num / den) * 100 : 0);

    // Optional: round to N decimals and keep as number
    const round = (v, n = 4) => Math.round(v * 10 ** n) / 10 ** n;

    const pct = ratePct(valueToCalcualteThroughRate, impressions);     // 0.0097222...
    return round(pct, 4)
}


// ! I removed the attribution window keys because it's not needed for the custom conversions
const calculateMetaInsightsForCustomConversions = (totalInsightEntity, currentInsightEntity, attributionWindowKeys) => {
    // Custom Conversion Metrics - offsite_conversion.custom['custom_conversion_id']
    const actions = currentInsightEntity.actions
    const action_values = currentInsightEntity.action_values
    const costPerActionType = currentInsightEntity.cost_per_action_type
    const conversions = currentInsightEntity.conversions
    const costPerConversion = currentInsightEntity.cost_per_conversion


    // We have the 'actions' and 'action_values' for the 'count'
    if (actions?.length) {
        for (const action of actions) {
            let finalValue = 0
            // if (action.action_type.includes('offsite_conversion.custom')) {
            if (action.action_type.includes('.custom')) {

                const customConversionId = action.action_type.split('.')[2]
                const customConversionKey = `custom_conversion_count_${customConversionId}`

                // if (attributionWindowKeys) {
                //     if (action) {
                //         for (const key of attributionWindowKeys) {
                //             finalValue += action[key] ? Number(action[key]) : 0
                //         }
                //     }
                // } else {
                finalValue = action.value ? Number(action.value) : 0
                // }

                // Add it to the total entity
                totalInsightEntity[customConversionKey] = finalValue
            }
        }
    }

    if (action_values?.length) {
        for (const action of action_values) {
            let finalValue = 0
            // if (action.action_type.includes('offsite_conversion.custom')) {
            if (action.action_type.includes('.custom')) {

                const customConversionId = action.action_type.split('.')[2]
                const customConversionKey = `custom_conversion_value_${customConversionId}`

                // if (attributionWindowKeys) {
                //     if (action) {
                //         for (const key of attributionWindowKeys) {
                //             finalValue += action[key] ? Number(action[key]) : 0
                //         }
                //     }
                // } else {
                finalValue = action.value ? Number(action.value) : 0
                // }

                // Add it to the total entity
                totalInsightEntity[customConversionKey] = finalValue
            }
        }
    }

    if (costPerActionType?.length) {
        for (const action of costPerActionType) {
            let finalValue = 0
            // if (action.action_type.includes('offsite_conversion.custom')) {
            if (action.action_type.includes('.custom')) {

                const customConversionId = action.action_type.split('.')[2]
                const customConversionKey = `custom_conversion_cost_per_${customConversionId}`

                // if (attributionWindowKeys) {
                //     if (action) {
                //         for (const key of attributionWindowKeys) {
                //             finalValue += action[key] ? Number(action[key]) : 0
                //         }
                //     }
                // } else {
                finalValue = action.value ? Number(action.value) : 0
                // }

                // Add it to the total entity
                totalInsightEntity[customConversionKey] = finalValue
            }
        }
    }

    // We also have the 'conversions' which are dynamic from each ad account
    if (conversions?.length) {
        for (const conversion of conversions) {
            let finalValue = 0
            const customConversionKey = `custom_conversion_${conversion.action_type}`
            const customConversionKeyThroughRate = `custom_conversion_through_rate_${conversion.action_type}`

            // if (attributionWindowKeys) {
            //     if (conversion) {
            //         for (const key of attributionWindowKeys) {
            //             finalValue += conversion[key] ? Number(conversion[key]) : 0
            //         }
            //     }
            // } else {
            finalValue = conversion.value ? Number(conversion.value) : 0
            // }

            // Add it to the total entity
            totalInsightEntity[customConversionKey] = totalInsightEntity[customConversionKey] ? totalInsightEntity[customConversionKey] + finalValue : finalValue
            totalInsightEntity[customConversionKeyThroughRate] = calcualteThroughRate(totalInsightEntity.impressions, totalInsightEntity[customConversionKey])
        }
    }

    if (costPerConversion?.length) {
        for (const conversion of costPerConversion) {
            // let finalValue = 0
            const customConversionKey = `custom_conversion_cost_per_${conversion.action_type}`
            const theValueCountKey = `custom_conversion_${conversion.action_type}`
            // console.log(conversion)

            // if (attributionWindowKeys) {
            //     if (conversion) {
            //         for (const key of attributionWindowKeys) {
            //             finalValue += conversion[key] ? Number(conversion[key]) : 0
            //         }
            //     }
            // } else {
            //     finalValue = conversion.value ? Number(conversion.value) : 0
            // }


            // Add it to the total entity
            // totalInsightEntity[customConversionKey] = totalInsightEntity[customConversionKey] ? totalInsightEntity[customConversionKey] + finalValue : finalValue
            totalInsightEntity[customConversionKey] = totalInsightEntity.spend ? totalInsightEntity.spend / totalInsightEntity[theValueCountKey] : 0
        }
    }
}

export const calculateMetaInsightsForInternalCustomMetrics = (totalInsightEntity, currentInsightEntity, internalCustomMetrics) => {
    if (!internalCustomMetrics?.length) {
        return
    }

    // Operator precedence: * and / have higher precedence than + and -
    const precedence = { '+': 1, '-': 1, '*': 2, '/': 2 }

    // Convert infix tokens to postfix notation (Shunting Yard algorithm)
    const toPostfix = (tokens) => {
        const output = []
        const operatorStack = []

        for (const token of tokens) {
            if (token.type === 'metric' || token.type === 'number') {
                output.push(token)
            } else if (token.type === 'operator') {
                while (
                    operatorStack.length > 0 &&
                    operatorStack[operatorStack.length - 1].type === 'operator' &&
                    precedence[operatorStack[operatorStack.length - 1].value] >= precedence[token.value]
                ) {
                    output.push(operatorStack.pop())
                }
                operatorStack.push(token)
            } else if (token.type === 'parenthesis' && token.value === '(') {
                operatorStack.push(token)
            } else if (token.type === 'parenthesis' && token.value === ')') {
                while (
                    operatorStack.length > 0 &&
                    !(operatorStack[operatorStack.length - 1].type === 'parenthesis' && operatorStack[operatorStack.length - 1].value === '(')
                ) {
                    output.push(operatorStack.pop())
                }
                operatorStack.pop() // Remove the '(' from stack
            }
        }

        // Pop remaining operators
        while (operatorStack.length > 0) {
            output.push(operatorStack.pop())
        }

        return output
    }

    // Evaluate postfix expression.
    // windowedDeps (optional) holds this metric's action-derived dependencies
    // summed under its own pinned attribution window; when a token's key is
    // present there it wins over the report-window total. Absent => behaves
    // exactly as before (reads the normal total).
    const evaluatePostfix = (postfixTokens, entity, windowedDeps) => {
        const stack = []

        for (const token of postfixTokens) {
            if (token.type === 'metric') {
                // With a windowed bucket, resolve leaves from the bucket and
                // expand ratio deps under the window; without one, unchanged.
                const value = windowedDeps
                    ? resolveWindowedMetricValue(token.metricKey, windowedDeps, entity)
                    : entity[token.metricKey]
                stack.push(typeof value === 'number' ? value : 0)
            } else if (token.type === 'number') {
                stack.push(Number(token.value) || 0)
            } else if (token.type === 'operator') {
                const b = stack.pop()
                const a = stack.pop()

                switch (token.value) {
                    case '+':
                        stack.push(a + b)
                        break
                    case '-':
                        stack.push(a - b)
                        break
                    case '*':
                        stack.push(a * b)
                        break
                    case '/':
                        stack.push(b !== 0 ? a / b : 0)
                        break
                    default:
                        stack.push(0)
                }
            }
        }

        return stack.length > 0 ? stack[0] : 0
    }

    for (const metric of internalCustomMetrics) {
        if (!metric.formulaTokens?.length) {
            continue
        }

        try {
            const postfixTokens = toPostfix(metric.formulaTokens)
            // If this metric pinned its own attribution window, resolve its
            // action-derived deps from the windowed bucket accumulated in
            // calculateInsights; otherwise this is undefined and eval is unchanged.
            const windowedDeps = totalInsightEntity.__windowedDeps?.[metric.key]
            const result = evaluatePostfix(postfixTokens, totalInsightEntity, windowedDeps)
            totalInsightEntity[metric.key] = result
        } catch (e) {
            console.error(`Error calculating custom metric "${metric.name}":`, e)
            totalInsightEntity[metric.key] = 0
        }
    }
}

// Meta's play curve: 22 points (0-14s per second, then 15-20/20-25/25-30/30-40/40-50/50-60 buckets, then 60s+).
const META_PLAY_CURVE_LENGTH = 22

// Merge the running play curve with the next ad's curve, weighted by each side's video plays,
// so a creative running in several ads gets one combined curve (not just the last ad's).
const mergePlayCurves = (totalInsightEntity, currentInsightEntity) => {

    const currentCurve = currentInsightEntity.video_play_curve_actions
    const currentValues = currentCurve?.[0]?.value
    const totalValues = totalInsightEntity.video_play_curve_actions?.[0]?.value
    if (!Array.isArray(currentValues)) {
        return totalInsightEntity.video_play_curve_actions || currentCurve
    }
    if (!Array.isArray(totalValues)) {
        return currentCurve
    }

    // totalInsightEntity.video_plays already includes the current ad at this point
    const currentPlays = Number(currentInsightEntity.video_plays) || 0
    const previousPlays = Math.max(0, (Number(totalInsightEntity.video_plays) || 0) - currentPlays)
    if (previousPlays + currentPlays === 0) {
        return currentCurve
    }

    const length = Math.max(currentValues.length, totalValues.length)
    const merged = []
    for (let i = 0; i < length; i++) {
        const previous = Number(totalValues[i]) || 0
        const current = Number(currentValues[i]) || 0
        merged.push(Math.round((previous * previousPlays + current * currentPlays) / (previousPlays + currentPlays)))
    }
    return [{ ...currentCurve[0], value: merged }]
}

// The actual transformation from platform's api to our local entity
export const calculateInsights = (totalInsightEntity, currentInsightEntity, attributionWindowKeys, benchmark, internalCustomMetrics, platform = 'meta') => {
    for (const metric of AllMetrics) {
        if (!metric.calculationFunction) {
            continue
        }

        platform = platform.toLowerCase()
        const extractor = metric.extractors?.[platform]
        if (extractor) {
            currentInsightEntity[metric.key] = extractor(currentInsightEntity, attributionWindowKeys)
        }

        // Calcualte the value from the benchmark if needed
        if (metric.calculateFromBenchmark && benchmark) {
            totalInsightEntity[metric.key] = metric.calculateFromBenchmarkFunction(benchmark, totalInsightEntity)
        } else {
            // Calcualte the value from the total entity and the current entity
            totalInsightEntity[metric.key] = metric.calculationFunction(totalInsightEntity, currentInsightEntity)
        }
    }

    if (platform === 'meta') {
        // Add to the total entity custom conversions with predefined logic
        calculateMetaInsightsForCustomConversions(totalInsightEntity, currentInsightEntity, attributionWindowKeys)
        // Non-mapped metrics (Insight we use but not on the regular report)
        totalInsightEntity.video_play_curve_actions = mergePlayCurves(totalInsightEntity, currentInsightEntity)
    }

    // TikTok has no per-second play curve like Meta's video_play_curve_actions —
    // it only exposes watch milestones. Carry them through so the retention chart
    // can synthesize a curve from them. Unlike the Meta curve above (a percentage
    // array, where overwriting is right), these are raw counts and this function
    // accumulates across insight rows — so they have to be summed or a creative
    // group with several ads would keep only the last ad's numbers.
    // Normalized because platform reaches here as either the lowercase value from
    // useRelevantIntegration or, in multi-account mode, the raw ad._provider —
    // which is the uppercase IntegrationProviders constant ('TIKTOK_ADS').
    const normalizedPlatform = String(platform || '').toLowerCase()
    if (normalizedPlatform === 'tiktok_ads' || normalizedPlatform === 'tiktok') {
        const videoPlayActions = Number(currentInsightEntity['video_play_actions'] || 0)
        if (videoPlayActions > 0) {
            const previous = totalInsightEntity.tiktok_video_retention || {}
            const sumMilestone = (key) => (previous[key] || 0) + Number(currentInsightEntity[key] || 0)
            totalInsightEntity.tiktok_video_retention = {
                video_play_actions: (previous.video_play_actions || 0) + videoPlayActions,
                video_watched_2s: sumMilestone('video_watched_2s'),
                video_watched_6s: sumMilestone('video_watched_6s'),
                video_views_p25: sumMilestone('video_views_p25'),
                video_views_p50: sumMilestone('video_views_p50'),
                video_views_p75: sumMilestone('video_views_p75'),
                video_views_p100: sumMilestone('video_views_p100'),
            }
        }
    }

    // Windowed custom metrics (opt-in): a custom metric can pin its own attribution
    // window via metric.attribution. For those, accumulate its action-derived,
    // summable dependencies under THAT window into a namespaced bucket, so the
    // formula can be evaluated against window-specific sums independent of the
    // report's global attribution window. Meta-only — other providers don't expose
    // per-window action breakdowns, so their custom metrics fall back to the normal
    // total (unchanged behavior). Metrics without `attribution` are skipped entirely,
    // so existing custom metrics are untouched.
    if (platform === 'meta' && internalCustomMetrics?.length) {
        for (const customMetric of internalCustomMetrics) {
            if (!customMetric.attribution) {
                continue
            }
            const windowKeys = []
            if (customMetric.attribution.click?.key) {
                windowKeys.push(customMetric.attribution.click.key)
            }
            if (customMetric.attribution.view?.key) {
                windowKeys.push(customMetric.attribution.view.key)
            }
            if (!windowKeys.length) {
                continue
            }
            // Collect the transitive windowable leaves reachable from this
            // metric's deps, so referencing a ratio (e.g. hook_rate) still
            // accumulates its underlying action count (3s_video_plays) under the
            // window. Ratios and window-independent fields are recomputed/kept at
            // eval time from these leaves + normal totals.
            const leaves = new Set()
            for (const baseKey of (customMetric.dependsOn || [])) {
                for (const leaf of collectWindowableLeaves(baseKey)) {
                    leaves.add(leaf)
                }
            }
            if (!leaves.size) {
                continue
            }
            if (!totalInsightEntity.__windowedDeps) {
                totalInsightEntity.__windowedDeps = {}
            }
            const bucket = totalInsightEntity.__windowedDeps[customMetric.key] || (totalInsightEntity.__windowedDeps[customMetric.key] = {})
            for (const leafKey of leaves) {
                const metaExtractor = metricByKey.get(leafKey)?.extractors?.meta
                if (!metaExtractor) {
                    continue
                }
                bucket[leafKey] = (bucket[leafKey] || 0) + Number(metaExtractor(currentInsightEntity, windowKeys) || 0)
            }
        }
    }

    // Calcualte the internal custom metrics
    calculateMetaInsightsForInternalCustomMetrics(totalInsightEntity, currentInsightEntity, internalCustomMetrics)

    return totalInsightEntity
}


// Report-level asset-breakdown grouping for Meta (Copy/Headline/Landing Page).
// Thin wrapper: injects the real calculateInsights into the dep-free builder.
export const generateAssetBreakdownReport = (adsData, groupType, attributionWindowKeys, benchmark, internalCustomMetrics, platform = 'meta') => {
    return buildAssetBreakdownGroups(adsData, groupType, calculateInsights, {
        attributionWindowKeys,
        benchmark,
        internalCustomMetrics,
        platform,
    })
}

// Ad x audience-segment matrix for Meta (age,gender or user_segment_key). Thin wrapper: injects the real
// calculateInsights into the dep-free builder. Powers the report's Audience breakdown.
export const generateAdAudienceMatrix = (adsData, attributionWindowKeys, benchmark, internalCustomMetrics, platform = 'meta', dimension) => {
    return buildAdAudienceMatrix(adsData, calculateInsights, {
        attributionWindowKeys,
        benchmark,
        internalCustomMetrics,
        platform,
        dimension,
    })
}


// This is to generate the reports for the ad groups on the 'top-performing' type report
export const generateAdGroupsReports = (adsDataByDates, attributionWindowKeys, benchmark, internalCustomMetrics, platform = 'meta') => {

    const adGroupByNameMap = {}
    const adGroupByName = []
    const adGroupByCopyMap = {}
    const adGroupByCopy = []
    const adGroupByHeadlineMap = {}
    const adGroupByHeadline = []
    const adGroupByLandingPageMap = {}
    const adGroupByLandingPage = []
    const adGroupByCreativeMap = {}
    const adGroupByCreative = []
    const adGroupsByAdUniqueMap = {}
    const adGroupsByAdUnique = []

    // sort the adsDataByDates to put the ad which their name includes 'copy' in the end.
    // Default to empty string when name is null — LinkedIn rows can have null names pre-Sub-B hydration.
    adsDataByDates = adsDataByDates.sort((a, b) => {
        const aName = (a.name || '').toLowerCase()
        const bName = (b.name || '').toLowerCase()
        if (aName.includes(' - copy') && !bName.includes(' - copy')) {
            return 1
        }
        return -1
    })

    for (const ad of adsDataByDates) {

        // Each ad row carries its native provider as `_provider` (stamped at
        // fetch / union time). Use it to pick the right metric extractors so
        // multi-account reports that mix providers (e.g. Meta + Axon) extract
        // each row's metrics from its own API shape — otherwise everything
        // would be read through the primary account's provider extractors and
        // the other-platform rows would come out blank for non-shared metrics.
        const adPlatform = ad._provider || platform

        // By ad uniqnue
        if (!adGroupsByAdUniqueMap[ad.id]) {
            adGroupsByAdUniqueMap[ad.id] = {
                id: ad.id,
                title: ad.name,
                adset_start_time: ad.adset_start_time,
                campaign_objective: ad.campaign_objective,
                ads: [ad],
                creatives: ad.creatives,
                groupType: 'ad_unique',
                created_time: ad.created_time,
                adType: ad.adType,
            }
            adGroupsByAdUnique.push(adGroupsByAdUniqueMap[ad.id])
        }

        for (const insight of ad.insights) {
            adGroupsByAdUniqueMap[ad.id] = calculateInsights(adGroupsByAdUniqueMap[ad.id], insight, attributionWindowKeys, benchmark, internalCustomMetrics, adPlatform)
            const dateKey = `by_dates_${insight.date_start}_${insight.date_stop}`
            if (!adGroupsByAdUniqueMap[ad.id][dateKey]) {
                adGroupsByAdUniqueMap[ad.id][dateKey] = {}
            }
            adGroupsByAdUniqueMap[ad.id][dateKey] = calculateInsights(adGroupsByAdUniqueMap[ad.id][dateKey], insight, attributionWindowKeys, benchmark, internalCustomMetrics, adPlatform)
        }


        // In case we want to hide all the ads without creative...
        // if (!ad.adCreative) {
        //     continue
        // }

        // By Ad Name
        // Default to empty string when name is null (LinkedIn rows can be null pre-Sub-B hydration).
        const lowerCaseName = (ad.name || '').toLowerCase().replace(/ - copy.*/, '')

        if (!adGroupByNameMap[lowerCaseName]) {
            adGroupByNameMap[lowerCaseName] = {
                id: ad.id, // This is very wired decision on me because i did not wanted to put more effort into this, this is of course wrong logic and the group should not have the first ad id but some other logic to determine this.
                title: ad.name,
                adset_start_time: ad.adset_start_time,
                campaign_objective: ad.campaign_objective,
                ads: [],
                creatives: [],
                groupType: 'ad_name',
                adType: ad.adType,
            }
            adGroupByName.push(adGroupByNameMap[lowerCaseName])
        }

        for (const insight of ad.insights) {
            adGroupByNameMap[lowerCaseName] = calculateInsights(adGroupByNameMap[lowerCaseName], insight, attributionWindowKeys, benchmark, internalCustomMetrics, adPlatform)
            const dateKey = `by_dates_${insight.date_start}_${insight.date_stop}`

            if (!adGroupByNameMap[lowerCaseName][dateKey]) {
                adGroupByNameMap[lowerCaseName][dateKey] = {}
            }
            adGroupByNameMap[lowerCaseName][dateKey] = calculateInsights(adGroupByNameMap[lowerCaseName][dateKey], insight, attributionWindowKeys, benchmark, internalCustomMetrics, adPlatform)
        }

        adGroupByNameMap[lowerCaseName].creatives = [...new Set([...adGroupByNameMap[lowerCaseName].creatives, ...ad.creatives])]
        adGroupByNameMap[lowerCaseName].ads.push(ad)

        // By Copy TODO. remember this might be an issue since x copies might show the same ad's insights so it might gonna need a breakdown for the 'body_asset' as well in the future.
        for (const copy of ad.copies) {
            const copyToLowerCase = copy.toLowerCase()
            if (!adGroupByCopyMap[copyToLowerCase]) {
                adGroupByCopyMap[copyToLowerCase] = {
                    // id: ad.id + "_" + copyToLowerCase,
                    id: copyToLowerCase,
                    title: copy,
                    adset_start_time: ad.adset_start_time,
                    campaign_objective: ad.campaign_objective,
                    ads: [],
                    creatives: [],
                    groupType: 'ad_copy',
                    adType: ad.adType,
                }
                adGroupByCopy.push(adGroupByCopyMap[copyToLowerCase])
            }

            for (const insight of ad.insights) {
                adGroupByCopyMap[copyToLowerCase] = calculateInsights(adGroupByCopyMap[copyToLowerCase], insight, attributionWindowKeys, benchmark, internalCustomMetrics, adPlatform)
                const dateKey = `by_dates_${insight.date_start}_${insight.date_stop}`
                if (!adGroupByCopyMap[copyToLowerCase][dateKey]) {
                    adGroupByCopyMap[copyToLowerCase][dateKey] = {}
                }
                adGroupByCopyMap[copyToLowerCase][dateKey] = calculateInsights(adGroupByCopyMap[copyToLowerCase][dateKey], insight, attributionWindowKeys, benchmark, internalCustomMetrics, adPlatform)
            }

            adGroupByCopyMap[copyToLowerCase].creatives = [...new Set([...adGroupByCopyMap[copyToLowerCase].creatives, ...ad.creatives])]
            adGroupByCopyMap[copyToLowerCase].ads.push(ad)
        }

        // By Headline
        for (const headline of ad.headlines) {
            const headlineToLowerCase = headline.toLowerCase()
            if (!adGroupByHeadlineMap[headlineToLowerCase]) {
                adGroupByHeadlineMap[headlineToLowerCase] = {
                    // id: ad.id + "_" + headlineToLowerCase,
                    id: headlineToLowerCase,
                    title: headline,
                    adset_start_time: ad.adset_start_time,
                    campaign_objective: ad.campaign_objective,
                    ads: [],
                    creatives: [],
                    groupType: 'ad_headline',
                    adType: ad.adType,
                }
                adGroupByHeadline.push(adGroupByHeadlineMap[headlineToLowerCase])
            }

            for (const insight of ad.insights) {
                adGroupByHeadlineMap[headlineToLowerCase] = calculateInsights(adGroupByHeadlineMap[headlineToLowerCase], insight, attributionWindowKeys, benchmark, internalCustomMetrics, adPlatform)
                const dateKey = `by_dates_${insight.date_start}_${insight.date_stop}`
                if (!adGroupByHeadlineMap[headlineToLowerCase][dateKey]) {
                    adGroupByHeadlineMap[headlineToLowerCase][dateKey] = {}
                }
                adGroupByHeadlineMap[headlineToLowerCase][dateKey] = calculateInsights(adGroupByHeadlineMap[headlineToLowerCase][dateKey], insight, attributionWindowKeys, benchmark, internalCustomMetrics, adPlatform)
            }

            adGroupByHeadlineMap[headlineToLowerCase].creatives = [...new Set([...adGroupByHeadlineMap[headlineToLowerCase].creatives, ...ad.creatives])]
            adGroupByHeadlineMap[headlineToLowerCase].ads.push(ad)
        }

        // By Landing Page
        for (const landingPage of ad.landingPages) {
            const landingPageToLowerCase = landingPage.toLowerCase()

            if (!adGroupByLandingPageMap[landingPageToLowerCase]) {
                adGroupByLandingPageMap[landingPageToLowerCase] = {
                    // id: ad.id + "_" + landingPageToLowerCase,
                    id: landingPageToLowerCase,
                    title: landingPage.replace('https://', '', ''),
                    adset_start_time: ad.adset_start_time,
                    campaign_objective: ad.campaign_objective,
                    ads: [],
                    creatives: [],
                    groupType: 'ad_landing_page',
                    adType: ad.adType,
                }
                adGroupByLandingPage.push(adGroupByLandingPageMap[landingPageToLowerCase])
            }

            for (const insight of ad.insights) {
                adGroupByLandingPageMap[landingPageToLowerCase] = calculateInsights(adGroupByLandingPageMap[landingPageToLowerCase], insight, attributionWindowKeys, benchmark, internalCustomMetrics, adPlatform)
                const dateKey = `by_dates_${insight.date_start}_${insight.date_stop}`
                if (!adGroupByLandingPageMap[landingPageToLowerCase][dateKey]) {
                    adGroupByLandingPageMap[landingPageToLowerCase][dateKey] = {}
                }
                adGroupByLandingPageMap[landingPageToLowerCase][dateKey] = calculateInsights(adGroupByLandingPageMap[landingPageToLowerCase][dateKey], insight, attributionWindowKeys, benchmark, internalCustomMetrics, adPlatform)
            }

            adGroupByLandingPageMap[landingPageToLowerCase].creatives = [...new Set([...adGroupByLandingPageMap[landingPageToLowerCase].creatives, ...ad.creatives])]
            adGroupByLandingPageMap[landingPageToLowerCase].ads.push(ad)
        }

        // Be Creative - Motion's version
        let creativeSet = ad.creatives.sort().join('_')
        if (!creativeSet) {
            // The 'no creative' means that the ad does not have any creative (or at least not in the API) probably a catalog ad or dynamic ad which we can't get the actual creative from the API
            creativeSet = 'no_creative' + '_' + ad.id
        }
        if (!adGroupByCreativeMap[creativeSet]) {
            adGroupByCreativeMap[creativeSet] = {
                id: creativeSet,
                title: ad.name,
                adset_start_time: ad.adset_start_time,
                campaign_objective: ad.campaign_objective,
                ads: [],
                creatives: [],
                groupType: 'ad_creative',
                insights: {},
                adType: ad.adType,
            }
            adGroupByCreative.push(adGroupByCreativeMap[creativeSet])
        }
        adGroupByCreativeMap[creativeSet].ads.push(ad)
        adGroupByCreativeMap[creativeSet].creatives = [...new Set([...adGroupByCreativeMap[creativeSet].creatives, ...ad.creatives])]

        for (const insight of ad.insights) {
            adGroupByCreativeMap[creativeSet] = calculateInsights(adGroupByCreativeMap[creativeSet], insight, attributionWindowKeys, benchmark, internalCustomMetrics, adPlatform)
            const dateKey = `by_dates_${insight.date_start}_${insight.date_stop}`
            if (!adGroupByCreativeMap[creativeSet][dateKey]) {
                adGroupByCreativeMap[creativeSet][dateKey] = {}
            }
            adGroupByCreativeMap[creativeSet][dateKey] = calculateInsights(adGroupByCreativeMap[creativeSet][dateKey], insight, attributionWindowKeys, benchmark, internalCustomMetrics, adPlatform)
        }


        // By Creative - Ron's version
        // for (const creativeId of ad.creatives) {

        // const isProbablyCarouselAd = ad.adCreative?.object_story_spec?.link_data?.child_attachments?.length
        // if (isProbablyCarouselAd) {
        //     continue
        // }

        // const creative = getCreativeFromState(creativeId)
        //
        // if (!adGroupByCreativeMap[creativeId]) {
        //     adGroupByCreativeMap[creativeId] = {
        //         id: creativeId,
        //         title: creative?.name || '- untitled -',
        //         ads: [],
        //         creatives: [creativeId],
        //         groupType: 'ad_creative',
        //         insights: {},
        //     }
        //     adGroupByCreative.push(adGroupByCreativeMap[creativeId])
        // }
        // adGroupByCreativeMap[creativeId].ads.push(ad)
        // // Calculate the insights by image / video breakdowns or without.
        // const insightsPerImageHash = ad.insights.filter(insight => (insight.image_asset?.hash && insight.image_asset?.hash === creative?.imageHash))
        // const insightsPerVideoId = ad.insights.filter(insight => (insight.video_asset?.id && insight.video_asset?.id === creative?.videoId))
        //
        //
        // if (!insightsPerImageHash.length && !insightsPerVideoId.length) {
        //     // TODO - ok, so we might want to indicate it to the user that we have insight about the creative which taken from the ad itself, not from the creative itself (because it's return empty from the API)
        //     adGroupByCreativeMap[creativeId].emptyBreakdown = true
        //     adGroupByCreativeMap[creativeId].isAffectedByMultipleCreatives = ad.creatives.length > 1
        //
        //     for (const insight of ad.insights) {
        //         adGroupByCreativeMap[creativeId] = calculateMetaInsights(adGroupByCreativeMap[creativeId], insight)
        //     }
        // }
        //
        // for (const insight of insightsPerImageHash) {
        //     adGroupByCreativeMap[creativeId] = calculateMetaInsights(adGroupByCreativeMap[creativeId], insight)
        // }
        //
        // for (const insight of insightsPerVideoId) {
        //     adGroupByCreativeMap[creativeId] = calculateMetaInsights(adGroupByCreativeMap[creativeId], insight)
        // }

        // if (creativeId === '6785091dac4d57cdca2b8caf') {
        //     console.log(ad)
        // }

        // }
    }

    return {
        adGroupByName,
        adGroupByCopy,
        adGroupByHeadline,
        adGroupByLandingPage,
        adGroupByCreative,
        adGroupsByAdUnique
    }

}

export const generateRawAdsBenchmarkReportByDate = (adsData, attributionWindowKeys, platform = 'meta') => {
    const allAdsNormalizedInsightsByDate = {}

    for (const ad of adsData) {
        if (!ad.insights?.length) {
            continue
        }
        // Use each ad's stamped provider so mixed-provider scopes (e.g. Meta +
        // Axon) extract metrics from each row's native API shape.
        const adPlatform = ad._provider || platform
        for (const insight of ad.insights) {
            const dateKey = `by_dates_${insight.date_start}_${insight.date_stop}`
            if (!allAdsNormalizedInsightsByDate[dateKey]) {
                allAdsNormalizedInsightsByDate[dateKey] = []
            }
            allAdsNormalizedInsightsByDate[dateKey].push(calculateInsights({}, insight, attributionWindowKeys, undefined, undefined, adPlatform))
        }
    }

    const finalBenchmarkByDate = {}
    for (const dateKey in allAdsNormalizedInsightsByDate) {
        finalBenchmarkByDate[dateKey] = generateCreativeBenchmarkReport(allAdsNormalizedInsightsByDate[dateKey])
    }

    return finalBenchmarkByDate
}

export const generateRawAdsBenchmarkReport = (adsData, attributionWindowKeys, platform = 'meta') => {
    const allAdsNormalizedInsights = []
    for (const ad of adsData) {
        if (!ad.insights?.length) {
            continue
        }
        // Use each ad's stamped provider so mixed-provider scopes extract
        // metrics from each row's native API shape.
        const adPlatform = ad._provider || platform
        for (const insight of ad.insights) {
            const normalizedInsight = calculateInsights({}, insight, attributionWindowKeys, undefined, undefined, adPlatform)
            allAdsNormalizedInsights.push(normalizedInsight)
        }
    }


    return generateCreativeBenchmarkReport(allAdsNormalizedInsights)

}

export const generateCreativeBenchmarkReport = (adGroupByCreative) => {

    // For video retention
    function calculateRetentionAverage(insightsArray) {
        if (insightsArray.length === 0) {
            return {}
        }

        const averageRetention = Array(META_PLAY_CURVE_LENGTH).fill(0)
        const allVideoRetentionValues = insightsArray
            .filter(obj => !!obj.video_play_curve_actions)
            .map(obj => obj.video_play_curve_actions)
            .filter(v => v?.[0]?.value)
            .map(v => v[0].value)

        for (let i = 0; i < META_PLAY_CURVE_LENGTH; i++) {
            // Only videos still playing at this point count. A video that already ended
            // reports 0 here, which would drag the average down for every longer video.
            const valuesForThisIndex = allVideoRetentionValues
                .map(v => Number(v[i]))
                .filter(val => Number.isFinite(val) && val > 0)

            if (valuesForThisIndex.length > 0) {
                averageRetention[i] = Math.round(valuesForThisIndex.reduce((sum, val) => sum + val, 0) / valuesForThisIndex.length)
            }
        }

        return averageRetention
    }

    function calculateBenchmark(insightsArray) {
        if (insightsArray.length === 0) return {}
        // Extract all insight keys from the first object
        const insightKeys = Object.keys(insightsArray[0])
        // Initialize benchmark object
        const benchmark = {}

        insightKeys.forEach((key) => {

            const values = insightsArray.map((obj) => obj[key]).filter(v => typeof v === "number")
            // const nonZeroValues = values.filter(v => v > 0)
            const nonZeroValues = values.filter(v => v > 0)

            if (values.length > 0) {
                benchmark[key] = {
                    highest: Math.max(...values),
                    average: values.reduce((sum, val) => sum + val, 0) / values.length,
                    lowest: Math.min(...values),
                    lowestNoneZero: values.length > 0 ? Math.min(...values) : 0, // If no nonzero values, set to 0
                    total: values.reduce((sum, val) => sum + val, 0)
                };
            }
        })

        return benchmark
    }

    if (!adGroupByCreative?.length) {
        return {}
    }

    const bench = calculateBenchmark(adGroupByCreative)
    bench.video_play_curve_actions = calculateRetentionAverage(adGroupByCreative)

    return bench
}

// This is to generate the report for the custom ad groups on the 'custom-groups-comparison' type report
export const generateCustomGroupAdReport = (customGroup, customGroupAdsData, attributionWindowKeys, benchmarkData, internalCustomMetrics, platform = 'meta') => {
    let customGroupOutput = {
        id: customGroup.id,
        title: customGroup.name,
        ads: customGroupAdsData,
        creatives: [... new Set(customGroupAdsData.map(ad => ad.creatives).flat())],
        groupType: 'custom_group',
    }

    for (const ad of customGroupAdsData) {
        // Use each ad's stamped provider so mixed-provider custom groups
        // (e.g. Meta + Axon ads in the same group) extract metrics from each
        // row's native API shape.
        const adPlatform = ad._provider || platform
        for (const insight of ad.insights) {
            customGroupOutput = calculateInsights(customGroupOutput, insight, attributionWindowKeys, benchmarkData, internalCustomMetrics, adPlatform)
            const dateKey = `by_dates_${insight.date_start}_${insight.date_stop}`
            if (!customGroupOutput[dateKey]) {
                customGroupOutput[dateKey] = {}
            }
            customGroupOutput[dateKey] = calculateInsights(customGroupOutput[dateKey], insight, attributionWindowKeys, benchmarkData, internalCustomMetrics, adPlatform)
        }
    }

    // Normalize the results to present in the UI
    if (!customGroupOutput.spend) {
        customGroupOutput = calculateInsights(customGroupOutput, {}, attributionWindowKeys, undefined, internalCustomMetrics, platform)
        customGroupOutput.isEmpty = true
    }

    return customGroupOutput

}

// Group-by naming convention: one row per property value (+ Unmatched). Reuses the
// custom-group metric math so ratios are recomputed from summed parts.
export const generateNamingConventionReport = (adsData, namingConvention, propertyName, attributionWindowKeys, benchmark, internalCustomMetrics, platform = 'meta') => {
    return bucketAdsByNamingConvention(adsData, namingConvention, propertyName).map(bucket => {
        const row = generateCustomGroupAdReport(
            { id: `nc:${propertyName}:${bucket.key}`, name: bucket.label },
            bucket.ads,
            attributionWindowKeys,
            benchmark,
            internalCustomMetrics,
            platform
        )
        row.groupType = NAMING_CONVENTION_GROUP_KEY
        return row
    })
}

export const filterRelevantAdsForSuperSegment = (superSegment, adsData) => {
    return adsData.filter(ad => ad.creatives.some(creativeId => superSegment.creatives.includes(creativeId)))
}

// export const generateSuperSegmentReport = (superSegment, filteredAdsData, attributionWindowKeys) => {
//     let superSegmentOutput = {
//         id: superSegment.query,
//         title: superSegment.query,
//         ads: filteredAdsData,
//         creatives: [...new Set(superSegment.creatives)],
//         groupType: 'super_segment',
//     }

//     for (const ad of filteredAdsData) {
//         for (const insight of ad.insights) {
//             superSegmentOutput = calculateMetaInsights(superSegmentOutput, insight, attributionWindowKeys)
//         }
//     }

//     // Normalize the results
//     if (!superSegmentOutput.length) {
//         superSegmentOutput = calculateMetaInsights(superSegmentOutput, {}, attributionWindowKeys)
//         superSegmentOutput.isEmpty = true
//     }

//     return superSegmentOutput
// }

export const sortCreativesByWatchRetentionData = (adGroupByCreative, getCreativeFromState) => {

    const chunks = [
        { start: 0, end: 5 },
        { start: 5, end: 10 },
        { start: 10, end: 15 },
        { start: 15, end: 20 }
    ]

    function average(arr) {
        if (!arr.length) {
            return 0
        }
        return arr.reduce((sum, v) => sum + v, 0) / arr.length;
    }

    const topVideosPerChunk = chunks.map(({ start, end }) => {

        const creativeMapPerChunk = {}
        const allCreativesForThisChunk = []

        const sorted = adGroupByCreative.sort((a, b) => {
            const videoPlayCurveA = a.video_play_curve_actions?.[0]?.value || [0]
            const videoPlayCurveB = b.video_play_curve_actions?.[0]?.value || [0]

            const avgA = average(videoPlayCurveA.slice(start, end))
            const avgB = average(videoPlayCurveB.slice(start, end))
            return avgB - avgA // descending
        })

        sorted.forEach(adGroup => {
            for (const creativeId of adGroup.creatives) {
                if (!creativeMapPerChunk[creativeId]) {
                    allCreativesForThisChunk.push(getCreativeFromState(creativeId))
                    creativeMapPerChunk[creativeId] = true
                }
            }
        })

        function shuffleArray(array) {
            const result = [...array]; // copy to avoid mutating original
            for (let i = result.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [result[i], result[j]] = [result[j], result[i]];
            }
            return result;
        }

        return shuffleArray(allCreativesForThisChunk)


    })


    return topVideosPerChunk
}

// Not in use - Legacy
// export const generateAdReport = (groupBy, adsDataByDates, getCreativeFromState) => {
//     const results = []
//     switch (groupBy.key) {
//         case 'ad_name':
//             const adsNameMap = {}
//             adsDataByDates.forEach(ad => {
//
//                 // Big TODO. solve this carousel shit already.
//                 // if (ad.name === 'Carousel Ad') {
//                 //     console.log(ad)
//                 // }
//
//                 if (!adsNameMap[ad.name]) {
//                     adsNameMap[ad.name] = {
//                         id: ad.id,
//                         image: ad.imageUrl ? ad.imageUrl : placeholderImage,
//                         initials: '',
//                         title: ad.name, // todo - strip the 'https://' etc.
//                         adsIds: {},
//                         ads: [],
//                         creatives: [],
//                         videoId: ad.videoId,
//                     }
//                     results.push(adsNameMap[ad.name])
//                 }
//
//                 adsNameMap[ad.name] = calculateMetaInsights(adsNameMap[ad.name], ad.insights)
//                 adsNameMap[ad.name].creatives = [...new Set([...adsNameMap[ad.name].creatives, ...ad.creatives])]
//
//                 if (!adsNameMap[ad.name].adsIds[ad.id]) {
//                     adsNameMap[ad.name].adsIds[ad.id] = 1
//                     adsNameMap[ad.name].ads.push(ad)
//                 } else {
//                     adsNameMap[ad.name].adsIds[ad.id]++
//                 }
//             })
//             break
//         case 'creative':
//             // const imageHashMap = {}
//             // const videoIdMap = {}
//             const adCreativesMap = {}
//             adsDataByDates.forEach(ad => {
//
//                 // The carousel fucks up all the report so i will deal with it in the future...
//                 const isProbablyCarouselAd = ad.adCreative?.object_story_spec?.link_data?.child_attachments?.length
//                 if (isProbablyCarouselAd) {
//                     return
//                 }
//
//
//                 for (const creativeId of ad.creatives) {
//                     if (!adCreativesMap[creativeId]) {
//                         const creative = getCreativeFromState(creativeId)
//
//                         // If creative type is video, find the insight where 'video_asset.video_id === creative.videoId'
//                         // If creative type is image, find the insight where 'image_asset.hash === creative.imageHash'
//
//
//                         if (creative?.name?.toLowerCase().includes('refresh your vanity')) {
//                             console.log(ad)
//                         }
//
//                         adCreativesMap[creativeId] = {
//                             // id: ad.id, // TODO.? creativeId
//                             id: creativeId,
//                             image: ad.imageUrl ? ad.imageUrl : placeholderImage,
//                             initials: '',
//                             title: creative?.name || '- untitiled -',
//                             adsIds: {},
//                             ads: [],
//                             videoId: ad.videoId,
//                             creatives: [creativeId]
//                         }
//                         results.push(adCreativesMap[creativeId])
//                     }
//                     adCreativesMap[creativeId] = calculateMetaInsights(adCreativesMap[creativeId], ad.insights)
//
//                     if (!adCreativesMap[creativeId].adsIds[ad.id]) {
//                         adCreativesMap[creativeId].adsIds[ad.id] = 1
//                         adCreativesMap[creativeId].ads.push(ad)
//                     } else {
//                         adCreativesMap[creativeId].adsIds[ad.id]++
//                     }
//                 }
//                 // console.log(adCreativesMap)
//
//
//                 // Currently, we disable carousel ads for this report. not sure why but this is what m does. (Maybe a Carousel ad fucks up the numbers when trying to dig into certain creative)
//                 // And also the 'child_attachments' is the indicator for carousel ads, not sure it's true but this is the closest i get so far. the 'object_type' just always showing 'SHARE' for some fucking reason
//
//                 //
//                 // // By Video ID
//                 // if (ad.videoId) {
//                 //     if (!videoIdMap[ad.videoId]) {
//                 //         videoIdMap[ad.videoId] = {
//                 //             id: ad.id,
//                 //             image: ad.imageUrl ? ad.imageUrl : placeholderImage,
//                 //             initials: '',
//                 //             title: ad.name,
//                 //             adsIds: {},
//                 //             ads: [],
//                 //             videoId: ad.videoId,
//                 //             creatives: []
//                 //         }
//                 //         results.push(videoIdMap[ad.videoId])
//                 //     }
//                 //
//                 //     videoIdMap[ad.videoId] = calculateMetaInsights(videoIdMap[ad.videoId], ad.insights)
//                 //     videoIdMap[ad.videoId].creatives = new Set([...videoIdMap[ad.videoId].creatives, ...ad.creatives])
//                 //
//                 //     if (!videoIdMap[ad.videoId].adsIds[ad.id]) {
//                 //         videoIdMap[ad.videoId].adsIds[ad.id] = 1
//                 //         videoIdMap[ad.videoId].ads.push(ad)
//                 //     } else {
//                 //         videoIdMap[ad.videoId].adsIds[ad.id]++
//                 //     }
//                 // }
//                 // // By Image Hash
//                 // ad.imageHash.forEach(hash => {
//                 //     if (!imageHashMap[hash]) {
//                 //         imageHashMap[hash] = {
//                 //             id: ad.id + "_" + hash,
//                 //             hash,
//                 //             image: ad.imageUrl ? ad.imageUrl : placeholderImage,
//                 //             initials: '',
//                 //             title: ad.name,
//                 //             adsIds: {},
//                 //             ads: [],
//                 //             videoId: ad.videoId,
//                 //         }
//                 //         results.push(imageHashMap[hash])
//                 //     }
//                 //
//                 //     imageHashMap[hash] = calculateMetaInsights(imageHashMap[hash], ad.insights)
//                 //
//                 //     if (!imageHashMap[hash].adsIds[ad.id]) {
//                 //         imageHashMap[hash].adsIds[ad.id] = 1
//                 //         imageHashMap[hash].ads.push(ad)
//                 //     } else {
//                 //         imageHashMap[hash].adsIds[ad.id]++
//                 //     }
//                 // })
//
//                 // This means the ad won't appear on the creative group by, still not sure why it misses this data of the actual creative but will understand that soon when we have Motion account
//                 // if (!ad.imageHash?.length && !ad.videoId) {
//                 //     console.log(ad)
//                 // }
//             })
//             break
//         case 'copy':
//             const copyMap = {}
//             adsDataByDates.forEach(ad => {
//                 ad.copies.forEach(copy => {
//                     if (!copyMap[copy]) {
//                         copyMap[copy] = { // TODO - to lower case?
//                             id: ad.id + "_" + copy,
//                             initials: copy[0],
//                             title: copy,
//                             adsIds: {},
//                             ads: [],
//                             creatives: [],
//                             videoId: ad.videoId,
//                         }
//                         results.push(copyMap[copy])
//                     }
//
//                     copyMap[copy] = calculateMetaInsights(copyMap[copy], ad.insights)
//                     copyMap[copy].creatives = [...new Set([...copyMap[copy].creatives, ...ad.creatives])]
//
//                     if (!copyMap[copy].adsIds[ad.id]) {
//                         copyMap[copy].adsIds[ad.id] = 1
//                         copyMap[copy].ads.push(ad)
//                     } else {
//                         copyMap[copy].adsIds[ad.id]++
//                     }
//                 })
//             })
//             break
//         case 'headline':
//             const headlineMap = {}
//             adsDataByDates.forEach(ad => {
//                 ad.headlines.forEach(headline => {
//                     if (!headlineMap[headline]) {
//                         headlineMap[headline] = {
//                             id: ad.id + "_" + headline,
//                             initials: headline[0],
//                             title: headline,
//                             adsIds: {},
//                             ads: [],
//                             videoId: ad.videoId,
//                             creatives: []
//                         }
//                         results.push(headlineMap[headline])
//                     }
//
//                     headlineMap[headline] = calculateMetaInsights(headlineMap[headline], ad.insights)
//                     headlineMap[headline].creatives = [...new Set([...headlineMap[headline].creatives, ...ad.creatives])]
//
//
//                     if (!headlineMap[headline].adsIds[ad.id]) {
//                         headlineMap[headline].adsIds[ad.id] = 1
//                         headlineMap[headline].ads.push(ad)
//                     } else {
//                         headlineMap[headline].adsIds[ad.id]++
//                     }
//                 })
//             })
//             break
//         case 'landing_page':
//             const landingPageMap = {}
//             adsDataByDates.forEach(ad => {
//                 ad.landingPages.forEach(page => {
//                     if (!landingPageMap[page]) {
//                         landingPageMap[page] = {
//                             id: ad.id + "_" + page,
//                             landingPage: page,
//                             initials: page[0].toUpperCase(),
//                             title: page, // todo - strip the 'https://' etc.
//                             adsIds: {},
//                             ads: [],
//                             videoId: ad.videoId,
//                             creatives: []
//                         }
//                         results.push(landingPageMap[page])
//                     }
//
//                     landingPageMap[page] = calculateMetaInsights(landingPageMap[page], ad.insights)
//                     landingPageMap[page].creatives = [...new Set([...landingPageMap[page].creatives, ...ad.creatives])]
//
//                     if (!landingPageMap[page].adsIds[ad.id]) {
//                         landingPageMap[page].adsIds[ad.id] = 1
//                         landingPageMap[page].ads.push(ad)
//                     } else {
//                         landingPageMap[page].adsIds[ad.id]++
//                     }
//                 })
//             })
//             break
//     }
//
//     return results
// }

export const generateAgeGenderAdsBreakdownReport = (adsByAgeAndGender, attributionWindowKeys, platform = 'meta') => {
    const mapAgeGender = {}

    for (const adInsights of adsByAgeAndGender) {
        const { gender, age } = adInsights

        if (!mapAgeGender[gender]) {
            mapAgeGender[gender] = {}
        }

        if (!mapAgeGender[gender][age]) {

            mapAgeGender[gender][age] = {}
        }
        mapAgeGender[gender][age] = calculateInsights(mapAgeGender[gender][age], adInsights, attributionWindowKeys, undefined, undefined, platform)
    }

    return mapAgeGender
}

export const generatePlacementAdsBreakdownReport = (adsByPlacement, attributionWindowKeys, platform = 'meta') => {
    const mapPlacement = {}

    for (const adInsights of adsByPlacement) {
        const key = `${adInsights.publisher_platform}_${adInsights.platform_position}`
        if (!mapPlacement[key]) {
            mapPlacement[key] = {}
        }

        mapPlacement[key] = calculateInsights(mapPlacement[key], adInsights, attributionWindowKeys, undefined, undefined, platform)
        mapPlacement[key].platform = adInsights.publisher_platform
        mapPlacement[key].position = adInsights.platform_position
    }

    return mapPlacement
}

export const generateBodyAssetAdsBreakdownReport = (adsByBodyAsset, attributionWindowKeys, platform = 'meta') => {
    const mapBodyAsset = {}

    for (const adInsights of adsByBodyAsset) {
        const { body_asset } = adInsights

        if (!mapBodyAsset[body_asset]) {
            mapBodyAsset[body_asset] = {}
        }

        mapBodyAsset[body_asset] = calculateInsights(mapBodyAsset[body_asset], adInsights, attributionWindowKeys, undefined, undefined, platform)
        mapBodyAsset[body_asset].bodyText = body_asset
    }

    return mapBodyAsset
}

// Old and shit
// export const generateAdsTimelineReportForSelectedMetrics = (adGroupsData, selectedMetrics) => {
//     // The output is [
//     //  {date: 'Jan 1', adGroup1_metric1: 0, adGroup1_metric2: 0, adGroup2_metric1: 0, adGroup2_metric2: 0}
//     //  {date: 'Jan 7', adGroup1_metric1: 0, adGroup1_metric2: 0, adGroup2_metric1: 0, adGroup2_metric2: 0},
//     // ]
//     // I want it to be [
//     //   { date: 'Jan 1', adGroup1: { metric1: 0, metric2: 0, metric3: 0, metric4: 0 } }
//     // ]
//
//     // Move this away...
//     function formatDate(dateString) {
//         const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
//
//         const [year, month, day] = dateString.split("-");
//         const formattedDate = `${months[parseInt(month, 10) - 1]} ${parseInt(day, 10)}`;
//
//         return formattedDate;
//     }
//
//
//     const final = []
//     const adsIdsToName = {}
//
//     for (const ad of data) {
//         adsIdsToName[ad.ad_id] = ad.ad_name
//     }
//
//     const adIds = [...new Set(data.map(d => d.ad_id))]
//     const dates = [...new Set(data.map(d => d.date_start))].sort((a, b) => new Date(a) - new Date(b))
//
//
//     // How messy is this code? from 1 to 10, 11
//     dates.forEach(date => {
//         const obj = { date: formatDate(date) }
//         adIds.forEach(adId => {
//             const adDataForThisDate = data.find(d => d.ad_id === adId && d.date_start === date)
//             if (adDataForThisDate) {
//                 for (const metric of selectedMetrics) {
//                     obj[`adGroup${adId}_${metric.key}`] = adDataForThisDate[metric.key] ? Number(adDataForThisDate[metric.key]) : 0
//                 }
//             }
//
//         })
//         final.push(obj)
//     })
//
//
//
//     // return { data: final, adsIdsToName }
// }

// New and shit
export const generateAdsTimelineReportForSelectedMetrics = (adGroupsData, attributionWindowKeys, platform = 'meta') => {
    // The output is [
    //   { date: 'Jan 1', adGroup1: { metric1: 0, metric2: 0, metric3: 0, metric4: 0 } }
    // ]

    // Move this away...
    function formatDate(dateString) {
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

        const [year, month, day] = dateString.split("-");
        const formattedDate = `${months[parseInt(month, 10) - 1]} ${parseInt(day, 10)}`;

        return formattedDate;
    }


    const map = {}
    const final = []
    for (const adGroup of adGroupsData) {

        for (const adBreakdown of adGroup.insightByDatesBreakdown) {
            for (const insightOfDate of adBreakdown) {

                if (!map[insightOfDate.date_start]) {
                    const record = {
                        dateLabel: formatDate(insightOfDate.date_start),
                        date_start: insightOfDate.date_start,
                        date_stop: insightOfDate.date_stop,
                        adGroupId: adGroup.id,
                        adGroupTitle: adGroup.title,
                        creatives: adGroup.creatives
                    }
                    final.push(record)
                    map[insightOfDate.date_start] = record
                }

                if (!map[insightOfDate.date_start][adGroup.id]) {
                    map[insightOfDate.date_start][adGroup.id] = {}
                }

                map[insightOfDate.date_start][adGroup.id] = calculateInsights(map[insightOfDate.date_start][adGroup.id], insightOfDate, attributionWindowKeys, undefined, undefined, platform)
            }
        }
    }

    return final.sort((a, b) => new Date(a.date_start) - new Date(b.date_start))
}

export const currencySymbol = {
    'usd': '$',
    'eur': '€',
    'ils': '₪',
    'gbp': '£'
}


// This is also prevent duplicates
export const extractAllCreativesIdsFromAdReport = (adReport) => {
    const allCreativesIds = []
    const allCreativesIdsMap = {}
    for (const ad of adReport) {
        if (ad.creatives) {
            for (const creativeId of ad.creatives) {
                if (!allCreativesIdsMap[creativeId]) {
                    allCreativesIdsMap[creativeId] = true
                    allCreativesIds.push(creativeId)
                }
            }
        }
    }

    return allCreativesIds
}

export const filterAdsData = (filtersToApply, data, filterLevel, getCreativeFromState) => {
    if (!filtersToApply.length) {
        return data
    }

    if (filterLevel === 'ad_group_level') {
        return data.filter(adGroup => {

            const filtersGroupsValidations = []
            for (const filtersGroup of filtersToApply) {
                let isValidGroup = false

                const allMetricFilters = filtersGroup.filter(filter => filter.optionId === 'metric')
                if (allMetricFilters.length === 0) {
                    isValidGroup = true // meaning it had only non-metric filters and we need to ignore it.
                }

                for (const filter of allMetricFilters) {
                    const filterValue = filter.value
                    const adGroupMetricValue = adGroup[filter.chosenMetric.key]

                    if (filter.operatorValue === 'greater') {
                        if (adGroupMetricValue > filterValue) {
                            isValidGroup = true
                        }
                    } else {
                        if (adGroupMetricValue < filterValue) {
                            isValidGroup = true
                        }
                    }
                }
                filtersGroupsValidations.push(isValidGroup)
            }

            const isAllGroupsValid = filtersGroupsValidations.filter(isValid => isValid).length === filtersToApply.length
            return isAllGroupsValid
        })
    }

    if (filterLevel === 'ad_level') {
        return data.filter(ad => {
            const filtersGroupsValidations = []
            for (const filtersGroup of filtersToApply) {

                let isValidGroup = false
                const allNonMetricFilters = filtersGroup.filter(filter => filter.optionId !== 'metric')
                if (allNonMetricFilters.length === 0) {
                    isValidGroup = true // meaning it had only metric filters and we need to ignore it.
                }

                for (const filter of allNonMetricFilters) {
                    // Each 'filter' of the 'filtersGroup' is an OR condition
                    const filterValue = typeof filter.value === 'string' ? filter.value.toLowerCase() : filter.value
                    const operatorValue = filter.operatorValue
                    switch (filter.optionId) {
                        case 'campaign_name': {
                            const insights = ad.insights?.length ? ad.insights : [{}]
                            for (const insight of insights) {
                                let isValidFilter = false
                                const campaignName = (insight.campaign_name || ad.campaign_name || ad.campaign || '')?.toLowerCase()
                                const campaignId = insight.campaign_id || ad.campaign_id || ad.campaignId

                                if (operatorValue === 'CONTAIN') {
                                    isValidFilter = campaignName.includes(filterValue)
                                } else if (operatorValue === 'NOT_CONTAIN') {
                                    isValidFilter = !campaignName.includes(filterValue)
                                } else if (operatorValue === 'is') {
                                    isValidFilter = campaignId && filterValue.includes(campaignId)
                                } else if (operatorValue === 'is_not') {
                                    isValidFilter = !campaignId || !filterValue.includes(campaignId)
                                }

                                if (isValidFilter) {
                                    isValidGroup = true
                                }
                            }
                            break
                        }
                        case 'adset_name': {
                            const insights = ad.insights?.length ? ad.insights : [{}]
                            for (const insight of insights) {
                                let isValidFilter = false
                                const adsetName = (insight.adset_name || ad.adset_name || '')?.toLowerCase()
                                const adsetId = insight.adset_id || ad.adset_id

                                if (operatorValue === 'CONTAIN') {
                                    isValidFilter = adsetName.includes(filterValue)
                                } else if (operatorValue === 'NOT_CONTAIN') {
                                    isValidFilter = !adsetName.includes(filterValue)
                                } else if (operatorValue === 'is') {
                                    isValidFilter = adsetId && filterValue.includes(adsetId)
                                } else if (operatorValue === 'is_not') {
                                    isValidFilter = !adsetId || !filterValue.includes(adsetId)
                                }

                                if (isValidFilter) {
                                    isValidGroup = true
                                }
                            }
                            break
                        }
                        case 'ad_name': {
                            const insights = ad.insights?.length ? ad.insights : [{}]
                            for (const insight of insights) {
                                const adName = (insight.ad_name || ad.title || ad.name || '')?.toLowerCase()
                                if (!adName) {
                                    continue
                                }
                                const isValidFilter = operatorValue === 'CONTAIN' ? adName.includes(filterValue) : !adName.includes(filterValue)
                                if (isValidFilter) {
                                    isValidGroup = true
                                }
                            }
                            break
                        }
                        case 'landing':
                            if (operatorValue === 'CONTAIN') {
                                const isValidFilter = ad.landingPages.find(landingPage => landingPage.toLowerCase().includes(filterValue))
                                if (isValidFilter) {
                                    isValidGroup = true
                                }
                            } else {
                                const isValidFilter = ad.landingPages.find(landingPage => !landingPage.toLowerCase().includes(filterValue))
                                if (isValidFilter || !ad.landingPages.length) {
                                    isValidGroup = true
                                }
                            }
                            break
                        case 'ad_type':
                            if (ad.adType) {
                                const isIncluded = filterValue.includes(ad.adType)
                                if (isIncluded && operatorValue === 'is') {
                                    isValidGroup = true
                                }
                                if (!isIncluded && operatorValue === 'is_not') {
                                    isValidGroup = true
                                }
                            }
                            break
                        case 'status':
                            if (ad.status) {
                                const isIncluded = filterValue.includes(ad.status)
                                if (isIncluded && operatorValue === 'is') {
                                    isValidGroup = true
                                }
                                if (!isIncluded && operatorValue === 'is_not') {
                                    isValidGroup = true
                                }
                            }
                            break
                        case 'ad_account': {
                            const accountId = ad._accountId
                            const isIncluded = !!accountId && filterValue.includes(accountId)
                            if (isIncluded && operatorValue === 'is') {
                                isValidGroup = true
                            }
                            if (!isIncluded && operatorValue === 'is_not') {
                                isValidGroup = true
                            }
                            break
                        }
                        case 'creative_ids':
                            if (ad.creatives?.length) {
                                const hasMatchingCreative = ad.creatives.some(creativeId => filterValue.includes(creativeId))
                                if (hasMatchingCreative && operatorValue === 'is') {
                                    isValidGroup = true
                                }
                                if (!hasMatchingCreative && operatorValue === 'is_not') {
                                    isValidGroup = true
                                }
                            }
                            break
                        case 'campaign_objective':
                            for (const insight of ad.insights) {
                                const objective = insight.campaign_objective || insight.objective

                                if (!objective) continue
                                const isIncluded = filterValue.includes(objective)
                                if (isIncluded && operatorValue === 'is') {
                                    isValidGroup = true
                                }
                                if (!isIncluded && operatorValue === 'is_not') {
                                    isValidGroup = true
                                }
                            }
                            break
                        case 'property':
                            if (ad.creatives?.length) {
                                const creatives = ad.creatives.map(getCreativeFromState)
                                // The filter is by the creatives set, if one of them have the property tag it will default to true
                                let matchingPropertyWithTag = false
                                for (const creative of creatives) {
                                    if (!creative) {
                                        handleError({ name: 'missing-creative-on-property-filters', creatives, ad })
                                        continue
                                    }
                                    if (creative.properties) {
                                        const propertyExists = creative.properties.find(property => property.id === filter.chosenProperty.id)
                                        if (propertyExists) {
                                            const foundMatchingTag = propertyExists.tags.find(tag => filter.value.find(filterTag => tag.label?.toLowerCase()?.trim() === filterTag.label?.toLowerCase()?.trim()))
                                            if (foundMatchingTag) {
                                                matchingPropertyWithTag = true
                                            }
                                        }
                                    }
                                }

                                if (operatorValue === 'CONTAIN' && matchingPropertyWithTag) {
                                    isValidGroup = true
                                }

                                if (operatorValue === 'NOT_CONTAIN' && !matchingPropertyWithTag) {
                                    isValidGroup = true
                                }
                            }
                            break
                        case 'naming_convention':
                            if (matchesNamingConventionFilter(ad.name, filter)) {
                                isValidGroup = true
                            }
                            break
                        case 'launch_date':
                            // const adsetStartTime = ad.adset_start_time
                            const adsetStartTime = ad.created_time
                            if (adsetStartTime) {
                                // Normalize dates to compare only the date part (ignore time)
                                const adsetDate = new Date(adsetStartTime)
                                const filterDate = new Date(filterValue)



                                // Set both dates to start of day for comparison
                                const adsetDateNormalized = new Date(adsetDate.getFullYear(), adsetDate.getMonth(), adsetDate.getDate())
                                const filterDateNormalized = new Date(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate())

                                let isValidFilter = false
                                if (operatorValue === 'before') {
                                    isValidFilter = adsetDateNormalized <= filterDateNormalized
                                } else if (operatorValue === 'after') {
                                    isValidFilter = adsetDateNormalized >= filterDateNormalized
                                }

                                if (isValidFilter) {
                                    isValidGroup = true
                                }
                            }
                            break
                    }
                }
                filtersGroupsValidations.push(isValidGroup)

            }
            const isAllGroupsValid = filtersGroupsValidations.filter(isValid => isValid).length === filtersToApply.length
            return isAllGroupsValid
        })
    }
}

export const extractNamingConventionValues = (namingConvention, data) => {
    if (!Array.isArray(namingConvention?.properties) || !Array.isArray(data)) {
        return {}
    }

    const result = {}
    namingConvention.properties.forEach(property => {
        const values = new Set(result[property.name] || [])
        data.forEach(item => {
            const value = extractNamingConventionValue(item?.name, namingConvention, property)
            if (value) {
                values.add(value)
            }
        })
        result[property.name] = Array.from(values).sort()
    })

    return result
}

export const extractNamingConventionValueFromAdName = (adName, namingConvention, propertyName = null) => {
    if (!adName || typeof adName !== 'string' || !Array.isArray(namingConvention?.properties)) {
        return propertyName ? null : {}
    }

    if (propertyName) {
        return extractNamingConventionValue(adName, namingConvention, getNamingConventionProperty(namingConvention, propertyName))
    }

    const result = {}
    namingConvention.properties.forEach(property => {
        result[property.name] = extractNamingConventionValue(adName, namingConvention, property)
    })
    return result
}

export const extractAllCampaignsFromAdReport = (data) => {

    const allCampaignsRelevantToAds = []
    const allCampaignsMap = {}

    for (const ad of data) {
        // Check insights array first (Meta/Google style)
        if (ad.insights?.length) {
            for (const insight of ad.insights) {
                if (insight.campaign_id) {
                    if (!allCampaignsMap[insight.campaign_id]) {
                        allCampaignsMap[insight.campaign_id] = true
                        allCampaignsRelevantToAds.push({ id: insight.campaign_id, name: insight.campaign_name })
                    }
                }
            }
        }

        // Also check top-level properties (Axon creative_set data)
        const topLevelCampaignId = ad.campaign_id || ad.campaignId
        if (topLevelCampaignId && !allCampaignsMap[topLevelCampaignId]) {
            allCampaignsMap[topLevelCampaignId] = true
            allCampaignsRelevantToAds.push({ id: topLevelCampaignId, name: ad.campaign_name || ad.campaign || ad.campaignName })
        }
    }

    return allCampaignsRelevantToAds

}

export const extractAllAdSetsFromAdReport = (data) => {
    const allAdSetsRelevantToAds = []
    const allAdSetsMap = {}

    for (const ad of data) {
        // Check insights array first (Meta/Google style)
        if (ad.insights?.length) {
            for (const insight of ad.insights) {
                if (insight.adset_id) {
                    if (!allAdSetsMap[insight.adset_id]) {
                        allAdSetsMap[insight.adset_id] = true
                        allAdSetsRelevantToAds.push({ id: insight.adset_id, name: insight.adset_name })
                    }
                }
            }
        }

        // Also check top-level properties (Axon creative_set data)
        const topLevelAdsetId = ad.adset_id || ad.adSetId
        if (topLevelAdsetId && !allAdSetsMap[topLevelAdsetId]) {
            allAdSetsMap[topLevelAdsetId] = true
            allAdSetsRelevantToAds.push({ id: topLevelAdsetId, name: ad.adset_name || ad.adsetName })
        }
    }

    return allAdSetsRelevantToAds
}

export const buildAdLinkOnAdsManager = (ad, adAccountId) => {

    // The ads-manager link goes to facebook.com/adsmanager — only valid for Meta ads.
    // For other providers we'd need a platform-specific URL (TikTok / Google / LinkedIn etc.)
    // that we don't currently build, so callers should hide the link UI when this returns {}.
    // _provider is stamped on each row in useAdsData / CreativePage / useViewWidgetData.
    if (ad?._provider && ad._provider !== 'Meta') {
        return {}
    }

    // option 1 is from the regular report || option 2 is from the breakdown report
    const adId = ad?.insights?.[0]?.ad_id || ad?.id
    const adSetId = ad?.insights?.[0]?.adset_id || ad?.adSetId
    const campaignId = ad?.insights?.[0]?.campaign_id || ad?.campaignId
    const campaignName = ad?.insights?.[0]?.campaign_name || ad?.campaignName
    const adsetName = ad?.insights?.[0]?.adset_name || ad?.adsetName

    if (!adId || !adSetId || !campaignId || !adAccountId) {
        return {}
    }

    return {
        link: `https://adsmanager.facebook.com/adsmanager/manage/ads/edit/standalone?act=${adAccountId}&selected_campaign_ids=${campaignId}&selected_adset_ids=${adSetId}&selected_ad_ids=${adId}`,
        adSetId: adSetId,
        campaignId: campaignId,
        adId: adId,
        campaignName: campaignName,
        adsetName: adsetName
    }

}

export const normalizeMetric = (value, metricData, currency = 'usd', formatToK = true, adsLength = 1) => {

    if (value == 0 || isNaN(Number(value))) {
        return 0
    }

    if (!value) { // !value means it's a custom conversion metric and it has not value (This is not the same as 0 !!)
        return '-'
    }

    if (!metricData) {
        return '-'
    }

    if (metricData.shouldDivideByAdsLength && value && adsLength) {
        return `${(value / adsLength).toFixed(2)}`
    }

    if (metricData.currency) {
        const relevantCurrency = currencySymbol[currency] ? currencySymbol[currency] : '$'
        const numberValue = value.toFixed(2)
        if (isNaN(Number(value)) || value === Infinity || value === -Infinity) {
            return 0
        }
        return `${relevantCurrency}${formatToK ? formatWithK(numberValue) : numberWithCommas(numberValue)}`
    }
    if (metricData.percentages && metricData.rate) {
        return `${value.toFixed(4)}%`
    }
    if (metricData.percentages) {
        return `${value.toFixed(2)}%`
    }
    if (metricData.time) {
        return `${Math.round(value)}${metricData.time}`
    }
    if (isNaN(value)) {
        return '-'
    }
    const finalValue = Number(value.toFixed(2))
    return formatToK ? formatWithK(finalValue) : numberWithCommas(finalValue)
    // return value
}